// Custom React Hooks for Vulcan View Dashboard

import { useState, useEffect, useCallback, useRef } from 'react';

// Export the new persistent mining data hook
export { default as useMiningDataPersistent } from './useMiningDataPersistent';
export { default as useMiningData } from './useMiningData';
export { default as useMiningDataManager } from './useMiningDataManager';
export { useIPScanner } from './useIPScanner';
export { default as useEarnings } from './useEarnings';
export type { EarningsView, HourlyBucket } from './useEarnings';

// Hook for API data fetching with auto-refresh
export const useApiData = <T>(
  fetchFunction: () => Promise<T>,
  refreshInterval: number = 30000,
  dependencies: any[] = []
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFunction, ...dependencies]);

  useEffect(() => {
    fetchData();

    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, refreshInterval]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
};

// Hook for local storage
export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue] as const;
};

// Hook for WebSocket connections
export const useWebSocket = (url: string) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<'Connecting' | 'Open' | 'Closing' | 'Closed'>('Closed');

  useEffect(() => {
    const ws = new WebSocket(url);
    setSocket(ws);
    setConnectionStatus('Connecting');

    ws.onopen = () => setConnectionStatus('Open');
    ws.onclose = () => setConnectionStatus('Closed');
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    return () => {
      ws.close();
    };
  }, [url]);

  const sendMessage = useCallback((message: any) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }, [socket]);

  return { lastMessage, connectionStatus, sendMessage };
};

// Hook for theme management
export const useTheme = () => {
  const [theme, setTheme] = useLocalStorage<'light' | 'dark'>('theme', 'dark');

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, [setTheme]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    
    // Update CSS custom properties for theme with Vulcanics Red
    const themeColors = theme === 'light' ? {
      '--color-primary': '#dc2626',
      '--color-background': '#ffffff',
      '--color-surface': '#f8fafc',
      '--color-card': '#ffffff',
      '--color-text-primary': '#0f172a',
      '--color-text-secondary': '#475569',
      '--color-border': '#e2e8f0',
      '--color-success': '#10b981',
      '--color-error': '#ef4444'
    } : {
      '--color-primary': '#dc2626',
      '--color-background': '#0f172a',
      '--color-surface': '#1e293b',
      '--color-card': '#334155',
      '--color-text-primary': '#f1f5f9',
      '--color-text-secondary': '#cbd5e1',
      '--color-border': '#475569',
      '--color-success': '#10b981',
      '--color-error': '#ef4444'
    };

    Object.entries(themeColors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }, [theme]);

  return { theme, toggleTheme };
};